import { Flex } from '@huspy/briks-web';
import useListAllOpportunities from '@modules/opportunities/entities/opportunity/query/useListAllOpportunities';
import { CasesRoute } from '@modules/opportunities/presentation/routes';
import { OPPORTUNITY_STATUS } from '@modules/opportunities/entities/opportunity/const';
import ControlPanel from '@modules/opportunities/presentation/pages/Cases/List/ControlPanel';
import CasesTable from '@modules/opportunities/presentation/pages/Cases/List/CasesTable';
import { useNavigate } from '@tanstack/react-router';
import { BUSINESS_IDENTIFIER } from '@modules/core/api/const';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { useTranslation } from 'react-i18next';
import { useLayoutStore } from '@modules/core/store';
import { useEffect } from 'react';
import { STATUS_GROUPS } from '@modules/opportunities/presentation/pages/const';
import { CaseSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/CaseSlido';
import { InfoWidget } from '@components/v2/InfoWidget';
import useMetaOpportunities from '@modules/opportunities/entities/opportunity/query/useMetaOpportunities';
import MainContentPageWrapper from '@components/v2/MainContentPageWrapper';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';

const mapStatusToBankApplicationStatuses = (status: string): string[] => {
  const map: any = {
    [STATUS_GROUPS.ACTIVE]: [
      BANK_APPLICATION_STATUS.docsReview,
      BANK_APPLICATION_STATUS.bankSubmission,
      BANK_APPLICATION_STATUS.salesQueries,
      BANK_APPLICATION_STATUS.creditQueries,
      BANK_APPLICATION_STATUS.preApproved,
      BANK_APPLICATION_STATUS.salesSubmittedToCredit,
      BANK_APPLICATION_STATUS.valuation,
      BANK_APPLICATION_STATUS.fol,
      BANK_APPLICATION_STATUS.disbursed
    ],
    [STATUS_GROUPS.NOT_SUBMITTED]: [
      OPPORTUNITY_STATUS.draft
    ],
    [STATUS_GROUPS.DECLINED]: [
      BANK_APPLICATION_STATUS.notProceeding
    ],
    [BANK_APPLICATION_STATUS.docsReview]: [
      BANK_APPLICATION_STATUS.docsReview,
      BANK_APPLICATION_STATUS.fillForms,
      BANK_APPLICATION_STATUS.docsReadyToSend
    ],
    [BANK_APPLICATION_STATUS.preApproved]: [
      BANK_APPLICATION_STATUS.preApproved,
      BANK_APPLICATION_STATUS.propertySelection
    ],
  };

  return map[status] ?? [status];
};

const prepareQuery = (q: Record<string, any>) => {
  const { status } = q;

  const mappedStatuses: string[] = status
    ? status.split('|').reduce((acc: string[], _status: string) => {
      const mappedStatus = mapStatusToBankApplicationStatuses(_status);
      return [...acc, ...mappedStatus];
    }, [])
    : [];

  return {
    ...q,
    ...(mappedStatuses.length > 0 ? { status: mappedStatuses.join('|') } : {}),
  };
};

export const CasesOverviewV2 = () => {
  const q = CasesRoute.useSearch();
  const { data, isLoading } = useListAllOpportunities(prepareQuery({ ...q, page_size: 7 }));
  const { data: metaOpportunities, isLoading: isMetaOpportunitiesLoading } = useMetaOpportunities();
  const { featureFlags, isFeatureActive } = useFeatureFlags();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { setHeaderContent } = useLayoutStore();
  useEffect(() => {
    if (isFeatureActive('opportunities.caseCreation')) {
      setHeaderContent(<CaseSlido />);
      return () => {
        setHeaderContent(null);
      };
    }
  }, [setHeaderContent, featureFlags, isFeatureActive]);

  // Cases should only be available for UAE
  if (APPLICATION_BUSINESS_IDENTIFIER !== BUSINESS_IDENTIFIER.AE_HUSPY) {
    navigate({ to: '/' });
  }

  return (
    <MainContentPageWrapper
      title={ t('opportunity.caseList.title') }
      subtitle={ t('opportunity.caseList.additionalTitle') }
      asideTitleContent={ (
        <Flex align='center' gap={ 3 }>
          <InfoWidget
            title={ t('opportunity.caseList.v2.stats.activeCases') }
            isLoading={ isMetaOpportunitiesLoading }
            content={ metaOpportunities?.stages_counts.active }
          />

          <InfoWidget
            title={ t('opportunity.caseList.v2.stats.completedCases') }
            isLoading={ isMetaOpportunitiesLoading }
            content={ metaOpportunities?.stages_counts.completed }
          />

          <InfoWidget
            title={ t('opportunity.caseList.v2.stats.cancelledCases') }
            isLoading={ isMetaOpportunitiesLoading }
            content={ metaOpportunities?.stages_counts.declined }
          />
        </Flex>
      ) }
    >
      <Flex justify='space-between' pt='6' pb='10'>
        <ControlPanel />
      </Flex>
      <Flex>
        <CasesTable
          data={ data }
          isLoading={ isLoading }
        />
      </Flex>
    </MainContentPageWrapper>
  );
};
